"use client";

import * as React from "react";
import { Link } from "@/i18n/navigation";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { useAppContext } from "@/contexts/app-context";
import { getAuthenticatedTabs, type AuthenticatedTab } from "@/lib/navigation-data";
import { LanguageSwitcher } from "@/components/language-switcher";
import DonistaWordmark from "@/components/icons/donista-wordmark";
import { InstallCtaButton } from "@/components/nav-menu/install-cta-button";

export function TabNavigation() {
  const pathname = usePathname();
  const { user, isAdmin } = useAppContext();
  const tNav = useTranslations("navigation");
  const tabs = getAuthenticatedTabs(tNav);

  const isActive = (href: string) => {
    if (href === "/home" && (pathname === "/" || pathname === "/home")) return true;
    return href !== "#" && href !== "/" && pathname.startsWith(href);
  };

  const visibleTabs = tabs.filter((tab) => {
    if (tab.adminOnly && !isAdmin) return false;
    // TODO: check voucherEnabled feature flag for vouchers tab
    if (tab.featureFlag === "voucherEnabled") return false;
    return true;
  });

  if (!user) return null;

  return (
    <>
      {/* Desktop: horizontal tabs in header */}
      <nav className="hidden md:flex items-center h-16 w-full border-b bg-background px-4 md:px-6 sticky top-0 z-50">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center w-full max-w-7xl mx-auto">
          <Link href={pathname.includes("/start") ? "/home" : "/start"} className="flex min-w-0 items-center gap-2 shrink-0">
            <DonistaWordmark />
          </Link>
          <div className="flex items-center justify-center gap-1">
            {visibleTabs.map((tab) => (
              <TabItem key={tab.key} tab={tab} isActive={isActive(tab.href)} variant="desktop" />
            ))}
          </div>
          <div className="flex min-w-0 items-center justify-end gap-2">
            <LanguageSwitcher />
            <InstallCtaButton variant="default" />
          </div>
        </div>
      </nav>

      {/* Mobile: slim sticky top bar (wordmark + install CTA) */}
      <div className="md:hidden flex h-14 shrink-0 items-center justify-between gap-2 px-4 sticky top-0 z-40 bg-background border-b">
        <Link href={pathname.includes("/start") ? "/home" : "/start"}>
          <DonistaWordmark />
        </Link>
        <InstallCtaButton variant="default" />
      </div>

      {/* Mobile: fixed bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 z-50 w-full h-16 border-t bg-background">
        <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, 1fr)` }}>
          {visibleTabs.map((tab) => (
            <TabItem key={tab.key} tab={tab} isActive={isActive(tab.href)} variant="mobile" />
          ))}
        </div>
      </nav>
    </>
  );
}

function TabItem({
  tab,
  isActive,
  variant,
}: {
  tab: AuthenticatedTab;
  isActive: boolean;
  variant: "desktop" | "mobile";
}) {
  const Icon = tab.icon;
  const LinkComponent = tab.adminOnly ? NextLink : Link;

  if (variant === "mobile") {
    return (
      <LinkComponent
        href={tab.href}
        className={cn(
          "flex flex-col items-center justify-center h-full w-full gap-1 p-2",
          isActive ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary",
        )}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="text-xs leading-tight">{tab.title}</span>
      </LinkComponent>
    );
  }

  return (
    <LinkComponent
      href={tab.href}
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        "hover:text-primary",
        isActive ? "text-primary" : "text-muted-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{tab.title}</span>
    </LinkComponent>
  );
}
